import {
  VirtualDetail,
  getBlanceFromValidator,
  cosmosToEvm,
  evmToCosmos,
} from '@mtt/wallet-cosmos';
import { useRequest } from 'ahooks';
import { set } from 'lodash-es';
import { BACKEND_HOST, DEV_BACKEND_HOST } from '@/constants';
import customMessage from '@/components/message/customMessage';
import { useGlobalContext } from '@/context/globalContext';

export const virtualDetail = new VirtualDetail(BACKEND_HOST);

export const useNodeDetail = (onGetNodeDetailError?: (e: any) => void) => {
  const { address } = useGlobalContext()
  // 使用 useRequest 来请求节点详情
  const {
    run: getNodeDetail,
    data: nodeDetail,
    error,
    loading: nodeDetailLoading,
  } = useRequest(
    async (addr: string) => {
      let delegatorAddress;
      try {
        delegatorAddress = evmToCosmos(address as string);
      } catch (error) {
        delegatorAddress = '';
      }

      if (addr) {
        const transformedAddress = cosmosToEvm(addr);
        const delegator = evmToCosmos(transformedAddress);
        const delegatorData = await virtualDetail.getNodeDetail({
          validatorAddress: addr,
        });

        const delegation = await virtualDetail.getDelegators({
          validatorAddress: addr,
        });

        const mineDelegatorList = await virtualDetail.getSelfDelegaterList({
          delegatorAddress: delegator,
        });

        const { data } = mineDelegatorList;

        let index;
        try {
          if (data.validators === null || data.validators.length === 0) {
            index = undefined;
          } else {
            index = data.validators.indexOf(addr);
          }
        } catch (error) {
          index = undefined;
        }

        const initTokens = index !== undefined ? data.amounts[index] : 0;

        set(delegatorData.data.detail, 'initTokens', initTokens);
        set(
          delegatorData.data.detail,
          'totalDelegators',
          delegation?.data?.pagination.total
        );
        const selfDelegateInfo = delegatorData.data.delegator.find(
          (item) => item.delegation.delegator_address === delegatorAddress
        );
        if (selfDelegateInfo) {
          set(delegatorData.data.detail, 'isDelegated', true);
          set(delegatorData.data.detail, 'selfDelegateInfo', selfDelegateInfo);
        } else {
          set(delegatorData.data.detail, 'isDelegated', false);
          set(delegatorData.data.detail, 'selfDelegateInfo', undefined);
        }

        return delegatorData;
      }
      return null;
    },
    {
      manual: true,
      onError: (e) => {
        onGetNodeDetailError && onGetNodeDetailError(e);
      },
    }
  );

  return {
    nodeDetail,
    nodeDetailLoading,
    getNodeDetail,
  };
};

export const useValidatorHistory = (onErrorEvent?: (e: any) => void) => {
  // 使用 useRequest 来请求节点详情
  const {
    run: fetchValidatorHistory,
    data: validatorHistory,
    error,
    loading: validatorHistoryLoading,
  } = useRequest(
    async (addr: string, pagination: { offset: string; limit: string }) => {
      if (addr) {
        const delegatorData = await virtualDetail.getValidatorHistory({
          validatorAddress: addr,
          pagination,
        });

        return delegatorData;
      }
      return null;
    },
    {
      manual: true,
      onError: (e) => {
        onErrorEvent && onErrorEvent(e);
      },
    }
  );

  return {
    fetchValidatorHistory,
    validatorHistory,
    validatorHistoryLoading,
  };
};

export const useDelegatorHistory = (onErrorEvent?: (e: any) => void) => {
  const { address: connectAddress } = useGlobalContext()
  // 使用 useRequest 来请求节点详情
  const {
    run: fetchDelegatorHistory,
    data: delegatorHistory,
    error,
    loading: delegatorHistoryLoading,
  } = useRequest(
    async (pagination: { offset: string; limit: string }) => {
      let address;
      try {
        address = await evmToCosmos(connectAddress as string);
      } catch (error) {
        address = '';
      }
      if (address) {
        const data = await virtualDetail.getDelegatorHistory({
          delegatorAddress: address,
          pagination,
        });

        return data;
      }
      return null;
    },
    {
      manual: true,
      onError: (e) => {
        onErrorEvent && onErrorEvent(e);
      },
    }
  );

  return {
    fetchDelegatorHistory,
    delegatorHistory,
    delegatorHistoryLoading,
  };
};

export const useRewardHistory = (onErrorEvent?: (e: any) => void) => {
  // 使用 useRequest 来请求节点详情
  const {
    run: fetchRewardHistory,
    data: rewardHistory,
    error,
    loading: rewardHistoryLoading,
  } = useRequest(
    async (addr: string) => {
      if (addr) {
        const data = await virtualDetail.getRewardHistory({
          validatorAddress: addr,
        });

        return data;
      }
      return null;
    },
    {
      manual: true,
      onError: (e) => {
        onErrorEvent && onErrorEvent(e);
      },
    }
  );

  return {
    fetchRewardHistory,
    rewardHistory,
    rewardHistoryLoading,
  };
};

export const useGetTxDetail = () => {
  const {
    run: getTxDetail,
    data: txDetail,
    error,
    loading: txLoading,
  } = useRequest(
    async (txHash: string) => {
      const data = await virtualDetail.getTxDetail(txHash);
      return data;
    },
    {
      manual: true,
    }
  );

  return {
    getTxDetail,
    txDetail,
    txLoading,
  };
};

export const useBlanceFromValidator = () => {
  const { data: totalBlance, run: fetchTotalBlance } = useRequest(
    async (validator: string, delegator: string) => {
      const data = await getBlanceFromValidator(validator, delegator);
      return data;
    }
  );
  return {
    totalBlance,
    fetchTotalBlance,
  };
};

export const useGetValidatorAlive = () => {
  const { data: validatorAliveData, run: fetchValidatorAlive } = useRequest(
    async (validator: string) => {
      const data = await virtualDetail.getValidatorAlive(validator);
      return data;
    }
  );
  return {
    validatorAliveData,
    fetchValidatorAlive,
  };
};

export const useValidatorContactInfo = (onSuccessSetInfo?: () => void) => {
  const {
    data: validatorContactInfoData,
    run: fetchValidatorContactInfo,
    loading: validatorContactInfoLoading,
  } = useRequest(async (validator: string) => {
    const data = await virtualDetail.getValidatorContactInfo(validator);
    return data;
  });

  const {
    data: setValidatorContactInfoData,
    run: fetchSetValidatorContactInfo,
    loading: setValidatorContactInfoLoading,
  } = useRequest(
    async (validator: string) => {
      const data = await virtualDetail.setValidatorContactInfo(validator);
      return data;
    },
    {
      manual: true,
      onSuccess(data, params) {
        if (data.code === 200) {
          customMessage.success(data?.msg || 'Success');
          onSuccessSetInfo?.();
        } else {
          customMessage.error(data?.msg || 'Sorry,something went wrong');
        }
      },
      onError(e, params) {
        customMessage.error(e?.msg || 'Sorry,something went wrong');
      },
    }
  );

  return {
    validatorContactInfoData,
    fetchValidatorContactInfo,
    validatorContactInfoLoading,
    setValidatorContactInfoData,
    fetchSetValidatorContactInfo,
    setValidatorContactInfoLoading,
  };
};
